// Template for /blog/<slug> pages.
// Converted from https://www.dbos.dev/blog/durable-queues.

import type { BlogPost } from '@content/types';
import { SiteLayout } from '@components/layout/SiteLayout';
import { FooterCtaBuildYourReliableBackend } from '@components/layout/FooterCtas';
import { Img } from '@components/cms/Img';
import { RecentPosts } from '@components/cms/RecentPosts';
import { SlideIn } from '@components/interactions';
import { cx } from '@components/webflow/element';
import { latestPosts } from '@content/blog';
import { longDate } from '@lib/format';

export function BlogPostPage({ post, body }: { post: BlogPost; body: string }) {
  const url = `https://www.dbos.dev/blog/${post.slug}`;
  return (
    <SiteLayout banner={true} footerCta={<FooterCtaBuildYourReliableBackend />}>
      <style
        dangerouslySetInnerHTML={{
          __html:
            '.author-item:not(:last-child)::after {\n    content: ", ";\n    color: var(--color-palette--color-lily-100);\n}\n\n.is-post {\n  display: -webkit-box;\n  -webkit-line-clamp: 2;\n  -webkit-box-orient: vertical;\n  overflow: hidden;\n}',
        }}
      />
      <section className="section_post-header">
        <SlideIn className="padding-global" as="div">
          <div className="container-large">
            <div className="padding-section-medium">
              <div className="max-width-large">
                <div className="margin-bottom margin-breadcrumb">
                  <div
                    data-wf--button--variant="tertiary-large"
                    className="button-wrapper w-variant-5a10be65-1a52-78d4-7d8b-5af0413eb67d"
                  >
                    <a href="/blog" className="button w-variant-5a10be65-1a52-78d4-7d8b-5af0413eb67d w-inline-block">
                      <div className="icon-arrow-left w-embed">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          {'\n'}
                          <path
                            d="M10.5892 5.58917L9.41083 4.41084L3.82167 10L9.41083 15.5892L10.5892 14.4108L7.01167 10.8333L15 10.8333L15 9.16667L7.01167 9.16667L10.5892 5.58917Z"
                            fill="var(--_color-scheme-july-2025---headings)"
                          />
                          {'\n'}
                        </svg>
                      </div>
                      <div className="text-block-4 w-variant-5a10be65-1a52-78d4-7d8b-5af0413eb67d">Back to posts</div>
                    </a>
                  </div>
                </div>
                <div className="margin-bottom margin-small">
                  <h1 className="heading-style-h2">{post.title}</h1>
                </div>
                <div className="margin-bottom margin-medium">
                  <div className="post_meta-wrapper">
                    <div className="blog-header_author-wrapper w-dyn-list">
                      <div role="list" className="blog-header_author-list w-dyn-items">
                        {post.authors.map((author, i) => (
                          <div role="listitem" className="blog-header_author-item w-dyn-item" key={i}>
                            <div className="post_author-wrapper">
                              <div className="post_author-image-wrapper">
                                <div className="blog-header_author-image-wrapper">
                                  <Img image={author.image} loading="lazy" className="blog-header_author-image" />
                                </div>
                              </div>
                              <div className="text-size-small">{author.name}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="post_date-wrapper">
                      <div className="text-size-small">{longDate(post.date)}</div>
                      <div className={cx('tagline-container text-size-small', !post.category && 'w-dyn-bind-empty')}>
                        {post.category}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="margin-bottom margin-small">
                  <div className="post_image-wrapper">
                    <Img image={post.image} loading="lazy" className="post_image-asset" />
                  </div>
                </div>
                <div className="margin-bottom margin-small">
                  <div className="post_meta-wrapper">
                    <a
                      href="https://www.google.com/preferences/source?q=https://www.dbos.dev"
                      target="_blank"
                      className="button secondary-small is-preferred w-inline-block"
                    >
                      <img
                        src="/assets/images/6a3887bc33e1d1fe520167bc_googlefavicon.avif"
                        loading="lazy"
                        alt=""
                        className="image-20"
                      />
                      <div className="text-size-regular">Add to Google</div>
                    </a>
                    <div className="w-layout-hflex share-post-wrapper">
                      <div className="share-post-copy is-post">Share this post</div>
                      <div className="social-link-code w-embed">
                        <a
                          className="social-link-wrapper"
                          target="_blank"
                          style={{ color: 'var(--color-palette--color-neutral-500)' }}
                          href={`https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${post.title}`}
                        >
                          {'\n  '}
                          <div className="social-link">
                            {'\n    '}
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 12 12"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              {'\n    \t'}
                              <path
                                d="M0 1.61369C0 1.22889 0.135139 0.911425 0.405405 0.661313C0.675672 0.411189 1.02703 0.286133 1.45946 0.286133C1.88417 0.286133 2.2278 0.409261 2.49035 0.655541C2.76061 0.909509 2.89575 1.24043 2.89575 1.64833C2.89575 2.01773 2.76448 2.32557 2.50193 2.57185C2.23166 2.82581 1.87645 2.9528 1.43629 2.9528H1.42471C0.999996 2.9528 0.656375 2.82581 0.393822 2.57185C0.13127 2.31788 0 1.99849 0 1.61369ZM0.150579 11.7147V4.0033H2.72201V11.7147H0.150579ZM4.14672 11.7147H6.71815V7.40878C6.71815 7.13942 6.74904 6.93162 6.81081 6.78541C6.91891 6.52374 7.08301 6.30248 7.30309 6.12163C7.52317 5.94077 7.79922 5.85034 8.13127 5.85034C8.99614 5.85034 9.42857 6.43139 9.42857 7.59349V11.7147H12V7.29334C12 6.15433 11.7297 5.29046 11.1892 4.70172C10.6486 4.11297 9.93437 3.8186 9.04633 3.8186C8.05019 3.8186 7.27413 4.24573 6.71815 5.09998V5.12307H6.70656L6.71815 5.09998V4.0033H4.14672C4.16216 4.24957 4.16988 5.01532 4.16988 6.30056C4.16988 7.58579 4.16216 9.3905 4.14672 11.7147Z"
                                fill="currentColor"
                              />
                              {'\n    '}
                            </svg>
                            {'\n  '}
                          </div>
                          {'\n'}
                        </a>
                      </div>
                      <div className="social-link-code w-embed">
                        <a
                          className="social-link-wrapper"
                          target="_blank"
                          style={{ color: 'var(--color-palette--color-neutral-500)' }}
                          href={`https://twitter.com/intent/tweet?url=${url}&hashtags=dbos`}
                        >
                          {'\n  '}
                          <div className="social-link">
                            {'\n    '}
                            <svg
                              width="11"
                              height="11"
                              viewBox="0 0 11 11"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              {'\n    '}
                              <g clipPath="url(#clip0_110_4018)">
                                {'\n    '}
                                <path
                                  d="M6.54209 4.65827L10.6343 0H9.66495L6.11015 4.04388L3.27306 0H0L4.29118 6.11565L0 11H0.969389L4.72092 6.72857L7.71774 11H10.9908M1.31926 0.715868H2.80852L9.66421 10.3193H8.17459"
                                  fill="currentColor"
                                />
                                {'\n    '}
                              </g>
                              {'\n    '}
                              <defs>
                                {'\n    '}
                                <clipPath id="clip0_110_4018">
                                  {'\n    '}
                                  <rect width="10.9908" height="11" fill="white" />
                                  {'\n    '}
                                </clipPath>
                                {'\n    '}
                              </defs>
                              {'\n    '}
                            </svg>
                            {'\n  '}
                          </div>
                          {'\n'}
                        </a>
                      </div>
                      <div className="social-link-code w-embed">
                        <a
                          className="social-link-wrapper"
                          target="_blank"
                          style={{ color: 'var(--color-palette--color-neutral-500)' }}
                          href={`https://www.facebook.com/sharer/sharer.php?u=${url}`}
                        >
                          {'\n  '}
                          <div className="social-link">
                            {'\n    '}
                            <svg
                              width="8"
                              height="12"
                              viewBox="0 0 8 12"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              {'\n    '}
                              <path
                                d="M4.9752 12V6.52624H6.905L7.19392 4.393H4.97515V3.03102C4.97515 2.4134 5.15527 1.99252 6.08556 1.99252L7.27202 1.99199V0.0840478C7.06681 0.0580973 6.36247 0 5.54315 0C3.83247 0 2.66131 0.994117 2.66131 2.81982V4.393H0.726562V6.52624H2.66131V11.9999H4.9752V12Z"
                                fill="currentColor"
                              />
                              {'\n    '}
                            </svg>
                            {'\n  '}
                          </div>
                          {'\n'}
                        </a>
                      </div>
                      <div className="social-link-code w-embed">
                        <a
                          className="social-link-wrapper"
                          target="_blank"
                          style={{ color: 'var(--color-palette--color-neutral-500)' }}
                          href={`mailto:?subject=Check%20out%20this%20article%20from%20DBOS&body=Check%20out%20this%20article%20from%20DBOS%3A%0A${url}`}
                        >
                          {'\n  '}
                          <div className="social-link">
                            {'\n    '}
                            <svg
                              width="12"
                              height="11"
                              viewBox="0 0 12 11"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              {'\n    '}
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M0.132783 1.00453C0.0473155 1.20425 0 1.42419 0 1.65517V8.68966C0 9.60381 0.741046 10.3448 1.65517 10.3448H10.3448C11.259 10.3448 12 9.60381 12 8.68966V1.65517C12 1.42419 11.9527 1.20426 11.8672 1.00454C11.8445 1.0333 11.819 1.06046 11.7906 1.08559L6.41126 5.84421C6.17644 6.05194 5.82358 6.05194 5.58875 5.84421L0.209444 1.08559C0.181032 1.06046 0.15547 1.0333 0.132783 1.00453ZM1.00444 0.132823C1.01377 0.140174 1.02294 0.147834 1.03195 0.155802L6.00001 4.55062L10.9681 0.155802C10.9771 0.147835 10.9862 0.140175 10.9956 0.132824C10.7958 0.0473306 10.5759 0 10.3448 0H1.65517C1.42415 0 1.20418 0.0473303 1.00444 0.132823Z"
                                fill="currentColor"
                              />
                              {'\n    '}
                            </svg>
                            {'\n  '}
                          </div>
                          {'\n'}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="margin-bottom margin-small">
                  <div className="padding-bottom padding-medium">
                    <main
                      className={cx('text-body-rich w-richtext', !body && 'w-dyn-bind-empty')}
                      dangerouslySetInnerHTML={{ __html: body }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </SlideIn>
      </section>
      <section className="section_posts">
        <SlideIn className="padding-global" as="div">
          <div className="container-large">
            <div>
              <div className="layout-2col_component is-testimonials">
                <div className="margin-bottom">
                  <div className="text-align-center">
                    <div className="margin-bottom margin-xsmall">
                      <div className="tagline-container_glow">
                        <div className="text-size-small tagline-container is-glow">Insights</div>
                      </div>
                    </div>
                    <div className="max-width-medium">
                      <div className="margin-bottom margin-small is-partner">
                        <h3 className="heading-style-h2">Recent articles</h3>
                      </div>
                      <div className="margin-bottom margin-small">
                        <p className="text-body-active">
                          {'The latest in durable execution, AI\u00a0workflows &\u00a0more.'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <RecentPosts posts={latestPosts.slice(0, 3)} />
              </div>
            </div>
          </div>
        </SlideIn>
      </section>
    </SiteLayout>
  );
}
